import React, { useCallback, useEffect, useState } from "react";
import {
  getAdminUserInferenceLogs,
  type AdminUserInferenceLog,
} from "../api/adminClient";
import AdminAssetImage from "../components/AdminAssetImage";

const inputClass =
  "px-4 py-2.5 border border-[var(--color-outline-variant)] rounded-lg text-sm bg-[var(--color-surface)] text-[var(--color-on-surface)] focus:outline-none focus:border-[var(--color-accent-blue)] focus:ring-1 focus:ring-[var(--color-accent-blue)] transition-colors transition-shadow duration-150";

const selectClass = inputClass;

const thClass = "pb-3 pr-4 font-medium text-left text-[var(--color-on-surface-variant)]";
const tdClass = "py-3 pr-4 text-[var(--color-on-surface)] vertical-middle align-middle";

function requestTypeLabel(t: string) {
  const map: Record<string, string> = {
    embed_image: "图片向量化",
    embed_text: "文本向量化",
    embed_fused: "融合向量化",
    ocr: "OCR 识别",
  };
  return map[t] || t;
}

function fmtBytes(n: number) {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} B`;
}

interface InferenceLogsPageProps {
  onAuthError?: () => void;
}

export default function InferenceLogsPage({ onAuthError }: InferenceLogsPageProps) {
  const [logs, setLogs] = useState<AdminUserInferenceLog[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [userIdFilter, setUserIdFilter] = useState("");
  const [requestType, setRequestType] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const uid = userIdFilter.trim() ? parseInt(userIdFilter.trim()) : undefined;
      const data = await getAdminUserInferenceLogs({
        page,
        limit: 20,
        user_id: uid && !isNaN(uid) ? uid : undefined,
        request_type: requestType || undefined,
      });
      setLogs(data.items ?? []);
      setPages(data.pages);
      setTotal(data.total);
    } catch (e: unknown) {
      setLogs([]);
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (msg.includes("401") || msg.includes("403") || msg.includes("管理员")) {
        onAuthError?.();
      }
    } finally {
      setLoading(false);
    }
  }, [page, userIdFilter, requestType, onAuthError]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1
            className="text-[28px] font-semibold text-[var(--color-on-surface)] mb-1"
            style={{ letterSpacing: "-0.02em" }}
          >
            处理记录
          </h1>
          <p className="text-[15px] text-[var(--color-on-surface-variant)]">
            用户在云端处理的所有图片和文字操作记录（保留原始内容、时间与模型）
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--color-secondary-container)] text-white disabled:opacity-50"
        >
          {loading ? "加载中..." : "刷新"}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-[var(--color-error-container)] text-[var(--color-on-error-container)] rounded-xl text-sm">
          {error}
        </div>
      )}

      <div className="bg-[var(--color-surface)] rounded-2xl p-6 border border-[var(--color-border-subtle)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="按用户ID过滤..."
              value={userIdFilter}
              onChange={(e) => {
                setUserIdFilter(e.target.value);
                setPage(1);
              }}
              className={`${inputClass} w-40`}
            />
            <select
              value={requestType}
              onChange={(e) => {
                setRequestType(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">全部处理类型</option>
              <option value="embed_image">图片向量化</option>
              <option value="embed_text">文本向量化</option>
              <option value="embed_fused">融合向量化</option>
              <option value="ocr">OCR 识别</option>
            </select>
          </div>
          {total > 0 && (
            <span className="text-sm font-normal text-[var(--color-on-surface-variant)]">
              共 {total} 条记录
            </span>
          )}
        </div>

        {logs.length === 0 ? (
          <div className="py-12 text-center text-sm text-[var(--color-on-surface-variant)]">
            {loading ? "加载中..." : "暂无处理操作记录数据"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-[var(--color-border-subtle)]">
                  <th className={thClass}>用户</th>
                  <th className={thClass}>处理类型</th>
                  <th className={thClass}>内容资源</th>
                  <th className={thClass}>识别结果</th>
                  <th className={thClass}>模型参数</th>
                  <th className={thClass}>缓存/费用</th>
                  <th className={thClass}>请求时间</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-[var(--color-border-subtle)] last:border-0 hover:bg-[var(--color-surface-container-low)]"
                  >
                    <td className={tdClass}>
                      <div className="font-medium text-[var(--color-on-surface)]">{row.username}</div>
                      <div className="text-xs text-[var(--color-outline)]">ID: {row.user_id}</div>
                    </td>
                    <td className={tdClass}>
                      <span className="font-medium">{requestTypeLabel(row.request_type)}</span>
                    </td>
                    <td className={`${tdClass} max-w-xs`}>
                      {row.content_type === "image" && row.content_hash ? (
                        <div className="flex items-center gap-3">
                          <AdminAssetImage contentHash={row.content_hash} />
                          <div className="text-xs text-[var(--color-on-surface-variant)]">
                            <div className="font-mono">{row.content_hash.slice(0, 10)}…</div>
                            {row.byte_size ? <div>{fmtBytes(row.byte_size)} ({row.mime_type})</div> : null}
                          </div>
                        </div>
                      ) : row.text_preview || row.content_type === "text" ? (
                        <div className="p-2.5 rounded-lg bg-[var(--color-surface-container-low)] border border-[var(--color-border-subtle)] text-xs text-[var(--color-on-surface)] whitespace-pre-wrap max-h-16 overflow-y-auto break-all font-mono">
                          {row.text_preview || "（文本资源）"}
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--color-outline)]">—</span>
                      )}
                      {row.auxiliary_preview ? (
                        <div className="mt-1 text-[10px] text-[var(--color-on-surface-variant)] line-clamp-2">
                          辅助: {row.auxiliary_preview}
                        </div>
                      ) : null}
                    </td>
                    <td className={`${tdClass} max-w-sm`}>
                      {row.request_type === "ocr" ? (
                        row.ocr_result_preview ? (
                          <div className="p-2.5 rounded-lg bg-[var(--color-surface-container-low)] border border-[var(--color-border-subtle)] text-xs text-[var(--color-on-surface)] whitespace-pre-wrap max-h-24 overflow-y-auto break-all font-mono">
                            {row.ocr_result_preview}
                          </div>
                        ) : (
                          <span className="text-xs text-[var(--color-outline)]">（无识别文本）</span>
                        )
                      ) : (
                        <span className="text-xs text-[var(--color-outline)]">—</span>
                      )}
                    </td>
                    <td className={`${tdClass} font-mono text-xs`}>
                      {row.provider && row.model ? (
                        <div>
                          <div>{row.provider}/{row.model}</div>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className={tdClass}>
                      <div className="text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${row.was_cache_hit ? "bg-emerald-500/15 text-emerald-600" : "bg-slate-200/50 text-slate-500"}`}>
                          {row.was_cache_hit ? "缓存命中" : "直接推理"}
                        </span>
                        {row.cost_cny ? (
                          <div className="mt-1 font-mono text-xs text-slate-500">
                            ¥{row.cost_cny.toFixed(4)}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className={`${tdClass} text-xs text-[var(--color-on-surface-variant)]`}>
                      {row.created_at?.replace("T", " ").slice(0, 19) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
            >
              上一页
            </button>
            <span className="text-sm text-[var(--color-on-surface-variant)] tabular-nums">
              {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-container-low)] disabled:opacity-40"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
